import axios from 'axios';
import { call, delay, put, select } from 'redux-saga/effects';

import * as AppActions from '../../../../actions';
import * as ListenerUtils from '../../../../lib/ListenerUtils';
import { putAndWait } from '../../../../sagas/TaskManager';
import ApplicationConstants from '../../constants';
import * as D from '../../deps';
import { isAdBlockerDetected as getIsAdBlockerDetected } from '../../utils/Application';

const spawn = D.taskManagerSpawnFactory(ApplicationConstants.DOMAIN_NAME);

export function* isAdBlockerDetected() {
  const OM_WEB_SRC = 'https://www.pandora.com/static/ads/omsdk-v1_3/omweb-video-v1_3/omweb-v1-m.js';
  const detectedThroughScriptLoading = yield detectedAdblockerThroughScriptLoading(OM_WEB_SRC);
  const detectedThroughNetworkCall = yield call(detectedAdblockerThroughNetworkCall);
  const detectedThroughBait = yield detectedAdblockerThroughBait();
  return detectedThroughScriptLoading || (detectedThroughBait && detectedThroughNetworkCall);
}

export function* detectedAdblockerThroughNetworkCall() {
  // We are loading static sailthru.json file.
  // Pretty much all adblocker has rules to block sailthru.json and if that is blocked it's a good indication of presence of adblocker.
  // Source: https://www.detectadblock.com/
  try {
    // Since sailthru.json is not hashed, fetch the file from `/webversion/version_number instead
    // If not, you get a version mismatch which leads to flex adds issues and PA bugs
    const assetLocation =
      ASSET_LOCATION === '/web-client/' ? ASSET_LOCATION : `/web-version/${__VERSION__}/`;
    const requestUrl = `${assetLocation}sailthru.json?v=${Date.now()}`;
    const timeout = D.AppConstants.NETWORK_PULSE_TIMEOUT_MS;
    const response = yield call(axios.get, requestUrl, {
      timeout,
      responseType: 'json',
    });
    // retrieving the version helps detect if the sailthru.json file has been stubbed/mocked by ad blocking software
    const { data } = response;
    return data.version !== __VERSION__;
  } catch (error) {
    const hasTimedOut = error.code === D.ServiceErrorConstants.CONNECTION_ABORTED;
    if (hasTimedOut) {
      // if there is a timeout will report as no ad block being used
      return false;
    }
    // if the request to sailthru.json fails we will assume the user is running ad blocking software
    return true;
  }
}

export function* openAdBlockerModal() {
  const { dispatch } = D.getStore();
  const modalConfig = {
    showMeHow: () => {
      dispatch(
        D.getInDomain('analytics.actions.logAdBlockerHammerModalEvent')({
          clicked: true,
          reason: 'clicked_cta',
        }),
      );
    },
    closeModal: () => {
      dispatch(D.AppActions.closeAdBlockerModal());
      dispatch(
        D.getInDomain('analytics.actions.logAdBlockerHammerModalEvent')({
          clicked: true,
          reason: 'dismissed',
        }),
      );
    },
  };
  yield put(
    D.getInDomain('analytics.actions.logAdBlockerHammerModalEvent')({
      clicked: false,
    }),
  );
  yield put(D.ModalActions.openAdBlockerModal(modalConfig));
}

export function* closeAdBlockerModal() {
  yield put(D.ModalActions.closeModal());
}

export function* adBlockerMonitor(source) {
  const adBlockCheckInterval = yield select(D.TunerVarUtils.getAdBlockCheckInterval);
  const shouldUseIntervalChecking = adBlockCheckInterval && source === 'app_start';
  do {
    const wasAdBlockedBefore = yield select(getIsAdBlockerDetected);
    const isAdBlocked = yield call(isAdBlockerDetected);
    yield put(D.AppActions.setIsAdBlockerDetected(isAdBlocked, source));
    const isFreeTierUser = yield select(ListenerUtils.shouldShowDisplayAds);
    const isDesktopUser = D.ClientUtils.isElectron();
    const shouldHandleAdBlocker = isFreeTierUser && !isDesktopUser;
    if (shouldHandleAdBlocker) {
      yield spawn(handleAdBlocker, isAdBlocked, wasAdBlockedBefore);
    }
    if (shouldUseIntervalChecking) yield delay(adBlockCheckInterval);
  } while (shouldUseIntervalChecking);
}

export function* handleAdBlocker(isAdBlocked, wasAdBlockedBefore) {
  const hasAdBlockStatusChanged = isAdBlocked !== wasAdBlockedBefore;
  if (hasAdBlockStatusChanged) {
    yield put(D.AppActions.setIsAntiAdBlockerHammerMode(isAdBlocked));
    yield call(initiateHammerAdBlockHandler);
  }
}

export function* initiateHammerAdBlockHandler() {
  const isAntiAdBlockHammerMode = yield select(
    D.getInDomain('application.utils.isAntiAdBlockerHammerModeDetected'),
  );
  const isAudioInitialized = yield select(D.getInDomain('playback.utils.isInitialized'));
  if (isAntiAdBlockHammerMode && isAudioInitialized && !isAdBlockerModalVisible()) {
    yield call(openAdBlockerModal);
    yield spawn(pauseAudioAndPlayAntiAdBlockSample);
  }
}

export function* pauseAudioAndPlayAntiAdBlockSample() {
  const antiAdBlockerData = D.getInDomain('listener.utils.getAntiAdBlockerEducationAudioUrl')();
  const isAudioPlaying = yield select(D.getInDomain('playback.utils.isAudioPlaying'));

  if (isAudioPlaying) {
    yield call(putAndWait, AppActions.UseCases.pauseTrack({ logPlaybackActionsAnalytics: false }));
    yield put(
      D.getInDomain('analytics.actions.logPlaybackInteractions')({
        action: 'ad_block_pause',
        userInitiated: false,
      }),
    );
  }
  yield put(D.getInDomain('analytics.actions.logAdBlockerAudioMessageCoachmark')());
  yield call(
    putAndWait,
    AppActions.SamplePlayer.playSample(antiAdBlockerData.url, antiAdBlockerData.resumeTrackOnEnd),
  );
}

function isAdBlockerModalVisible() {
  return document.getElementsByClassName('Modal__container__body--adBlocker').length > 0;
}

export function* detectAdBlockerWatcher({ payload }) {
  yield call(adBlockerMonitor, payload.source);
}

export function detectedAdblockerThroughBait() {
  const bait = createBaitElement();
  window.document.body.appendChild(bait);
  const detected = isElementBlocked(bait);
  window.document.body.removeChild(bait);

  return detected;
}

export function createBaitElement() {
  const bait = document.createElement('div');

  bait.setAttribute(
    'class',
    'pub_300x250 pub_300x250m pub_728x90 text-ad textAd text_ad text_ads text-ads text-ad-links ad-text adSense adBlock adContent adBanner',
  );
  bait.setAttribute(
    'style',
    'width: 1px !important; height: 1px !important; position: absolute !important; left: -10000px !important; top: -1000px !important;',
  );

  return bait;
}

export function isElementBlocked(el) {
  const elemCS = window.getComputedStyle(el, null);
  return el.offsetParent === null || elemCS.display === 'none' || elemCS.visibility === 'hidden';
}

function detectedAdblockerThroughScriptLoading(src) {
  const adBlockDetectorIframe = document.createElement('iframe');
  adBlockDetectorIframe.setAttribute('id', 'ad-blocker-detector');
  return new Promise((resolve, reject) => {
    const html = `
        <body style='margin: 0; overflow: hidden;'>
          <script async
                  src= ${src}
                  onload= 'notifyParent(false)'
                  onerror= 'notifyParent(true)'></script>
          <script>
              let iframeRef;
              function load(iframe) {
                iframeRef = iframe
              }
              function notifyParent(detected) {
                const customEvent = new CustomEvent('adBlockerDetected', { detail: detected });
                iframeRef.dispatchEvent(customEvent);
              }
          </script>
        </body>
        `;
    document.body.appendChild(adBlockDetectorIframe);
    adBlockDetectorIframe.contentWindow.document.write(html);
    adBlockDetectorIframe.contentWindow.load(adBlockDetectorIframe);

    adBlockDetectorIframe.addEventListener('adBlockerDetected', ({ detail }) => {
      adBlockDetectorIframe.remove();
      resolve(detail);
    });
  });
}
